import React, { useEffect, useRef, useState } from "react";
import { Autocomplete } from "@mui/material";

const INTERACTION_WINDOW_MS = 750;
const PAGE_ENTRY_GUARD_MS = 1000;

/**
 * Profile selects stay closed until a fresh pointer or keyboard interaction
 * reaches the control. Restored focus, async option hydration, and other
 * delayed MUI open requests are ignored.
 */
const ProfileAutocomplete = ({
  onBlur,
  onClose,
  onOpen,
  onMouseDown,
  onKeyDown,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const interactionStartedAtRef = useRef(0);
  const mountedAtRef = useRef(Date.now());

  const clearInteraction = () => {
    interactionStartedAtRef.current = 0;
  };

  const armForInteraction = () => {
    interactionStartedAtRef.current = Date.now();
  };

  const hasFreshInteraction = () => {
    const startedAt = interactionStartedAtRef.current;
    clearInteraction();
    return startedAt > 0 && Date.now() - startedAt <= INTERACTION_WINDOW_MS;
  };

  useEffect(() => {
    clearInteraction();
    setOpen(false);
  }, []);

  const handleOpen = (event) => {
    // A pointer event from the navigation click can land on this control
    // after it mounts, so a fresh mouse-down alone is not enough to prove
    // intent during page entry. Never open during that initial window.
    const intentionalInteraction = hasFreshInteraction();
    if (Date.now() - mountedAtRef.current < PAGE_ENTRY_GUARD_MS) return;
    if (!intentionalInteraction) return;
    setOpen(true);
    onOpen?.(event);
  };

  const handleClose = (event, reason) => {
    clearInteraction();
    setOpen(false);
    onClose?.(event, reason);
  };

  return (
    <Autocomplete
      {...props}
      open={open}
      openOnFocus={false}
      onOpen={handleOpen}
      onClose={handleClose}
      // Capture runs before MUI's internal input handlers request opening.
      onMouseDownCapture={() => armForInteraction()}
      onKeyDownCapture={(event) => {
        if (
          event.key !== "Tab" &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.altKey
        ) armForInteraction();
      }}
      onMouseDown={onMouseDown}
      onKeyDown={onKeyDown}
      onBlur={(event) => {
        clearInteraction();
        onBlur?.(event);
      }}
    />
  );
};

export default ProfileAutocomplete;
